import React, { useState } from 'react';
import Modal from '../../../ui-kit/Modal';
import Button from '../../../ui-kit/Button';
import { Star, AlertTriangle, Award } from 'lucide-react';
import { FEEDBACK_RESULTS, NEGATIVE_FEEDBACK } from '../logic/telesales';
import { TelesalesService } from '../services/telesalesService';

const inputCls = 'w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm';

const FeedbackCallModal = ({ isOpen, onClose, customer, currentUser, onSaved, onCreateComplaint }) => {
    const mobile = customer?.primaryMobile || customer?.mobile;
    const [f, setF] = useState({ productUsed: customer?.favoriteProduct || '', usageDuration: '', result: '', rating: 0, feedback: '', photoAvailable: false, permissionToUse: false, followUpRequired: false });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);
    const set = (k, v) => setF(s => ({ ...s, [k]: v }));
    const negative = NEGATIVE_FEEDBACK.has(f.result);

    const save = async () => {
        if (!f.result) { alert('Select a feedback result.'); return; }
        setBusy(true);
        try {
            const r = await TelesalesService.addFeedbackCall(mobile, f, currentUser);
            setDone(r);
            onSaved?.();
        } catch (e) { alert(e.message); } finally { setBusy(false); }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Feedback Call" className="max-w-xl">
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                <div className="grid grid-cols-2 gap-2">
                    <label className="text-xs text-slate-500">Product used
                        <input className={inputCls} value={f.productUsed} onChange={e => set('productUsed', e.target.value)} /></label>
                    <label className="text-xs text-slate-500">Usage duration
                        <input className={inputCls} placeholder="e.g. 3 weeks" value={f.usageDuration} onChange={e => set('usageDuration', e.target.value)} /></label>
                    <label className="text-xs text-slate-500">Result *
                        <select className={inputCls} value={f.result} onChange={e => set('result', e.target.value)}><option value="">Select…</option>{FEEDBACK_RESULTS.map(t => <option key={t}>{t}</option>)}</select></label>
                    <div>
                        <span className="text-xs text-slate-500">Rating</span>
                        <div className="flex gap-1 mt-1">{[1, 2, 3, 4, 5].map(n => <Star key={n} size={20} onClick={() => set('rating', n)} className={`cursor-pointer ${n <= f.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />)}</div>
                    </div>
                </div>
                <label className="text-xs text-slate-500 block">Customer feedback
                    <textarea className={inputCls} rows={2} value={f.feedback} onChange={e => set('feedback', e.target.value)} /></label>
                <div className="flex flex-wrap gap-4 text-sm">
                    <label className="flex items-center gap-1"><input type="checkbox" checked={f.photoAvailable} onChange={e => set('photoAvailable', e.target.checked)} /> Before/After photo</label>
                    <label className="flex items-center gap-1"><input type="checkbox" checked={f.permissionToUse} onChange={e => set('permissionToUse', e.target.checked)} /> Permission to use</label>
                    <label className="flex items-center gap-1"><input type="checkbox" checked={f.followUpRequired} onChange={e => set('followUpRequired', e.target.checked)} /> Follow-up required</label>
                </div>

                {f.result && negative && (
                    <div className="flex items-center justify-between gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-2">
                        <span className="flex items-center gap-2"><AlertTriangle size={15} /> Negative feedback — create a complaint / doctor case?</span>
                        <Button size="sm" variant="outline" onClick={() => onCreateComplaint?.(f)}>Create Ticket</Button>
                    </div>
                )}
                {f.result === 'Good Result' && (
                    <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg p-2">
                        <Award size={15} /> Positive — {f.permissionToUse ? 'will be marked a testimonial candidate.' : 'tick "Permission to use" to mark as testimonial candidate.'}
                    </div>
                )}

                {done ? (
                    <div className="text-sm text-center text-green-600 py-2">Saved. {done.testimonial ? 'Marked testimonial candidate.' : done.negative ? 'Flagged negative.' : ''}
                        <div className="mt-2"><Button size="sm" onClick={onClose}>Close</Button></div>
                    </div>
                ) : (
                    <div className="flex justify-end gap-2 pt-1">
                        <Button variant="outline" onClick={onClose}>Cancel</Button>
                        <Button onClick={save} disabled={busy}>Save Feedback</Button>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default FeedbackCallModal;
